import { useCallback, useEffect, useState } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Confirm } from "@/shared/ui";
import {
  characterByIdSelector,
  selectedCharIdSelector,
  setSelectedCharId,
} from "@/domains/account/slice";
import useDeleteCharacter from "@/domains/account/hooks/useDeleteCharacter";
import useGetCharacters from "@/domains/account/hooks/useGetCharacters";
import AccountCharDeleteConfirmName from "@/domains/account/components/char/delete/AccountCharDeleteConfirmName.tsx";
import AccountCharDeleteTrigger from "@/domains/account/components/char/delete/AccountCharDeleteTrigger.tsx";

const deleteModalInit = {
  confirm: false,
  confirmName: false,
  success: false,
};

export default function AccountCharDeleteContainer() {
  const disptach = useAppDispatch();
  const { getCharacters } = useGetCharacters();
  const { deleteHandler, isPending, isCompleted, error } = useDeleteCharacter();
  const [deleteModals, setDeleteModals] = useState<typeof deleteModalInit>(deleteModalInit);

  const selectedCharId = useAppSelector(selectedCharIdSelector);
  const character = useAppSelector((state) => characterByIdSelector(state, selectedCharId));

  const closeModals = useCallback(() => {
    setDeleteModals(deleteModalInit);
  }, []);

  const characterName = [character?.firstName || "", character?.lastName || ""].join(" ");

  const onDelete = useCallback(() => {
    setDeleteModals({ ...deleteModalInit, confirm: true });
  }, []);

  const onConfirmDelete = useCallback(() => {
    setDeleteModals({ ...deleteModalInit, confirmName: true });
  }, []);

  const onConfirmDeleteName = useCallback(() => {
    setDeleteModals({ ...deleteModalInit, success: true });
  }, []);

  const onCloseSuccess = useCallback(async () => {
    disptach(setSelectedCharId(null));
    await getCharacters();
    closeModals();
  }, []);

  const onSubmit = useCallback(
    async (password: string) => {
      if (character) {
        await deleteHandler({
          characterId: character.id,
          firstName: character.firstName,
          lastName: character.lastName,
          password,
        });
      }
    },
    [character],
  );

  useEffect(() => {
    if (isCompleted) {
      onConfirmDeleteName();
    }
  }, [isCompleted, onConfirmDeleteName]);

  return (
    <>
      <AccountCharDeleteTrigger onClick={onDelete} />
      <Confirm open={deleteModals.confirm} onConfirm={onConfirmDelete} onCancel={closeModals}>
        <div className="text-16 font-bold text-white text-center w-full">
          Вы точно хотите удалить персонажа <span className="text-accent">{characterName}?</span>
        </div>
      </Confirm>
      <AccountCharDeleteConfirmName
        characterName={characterName}
        onSubmit={onSubmit}
        open={deleteModals.confirmName}
        onCancel={closeModals}
        pending={isPending}
        error={error}
      />
      <Confirm
        open={deleteModals.success}
        onConfirm={onCloseSuccess}
        onCancel={onCloseSuccess}
        showCancel={false}
        disableAnimation
      >
        <div className="text-16 font-bold text-white text-center w-full">
          Персонаж <span className="text-accent">{characterName}</span> удален, его можно
          восстановить в течении 30 дней
        </div>
      </Confirm>
    </>
  );
}
